<script>
    import { objects, brfListLoading, searchTerm, redovisning, answer, question } from '../store/store.js';
    import { get } from "svelte/store";
    import { fetchBrf } from '../utils/utils.js';
	import { fetchBrfs } from '../utils/search.js';
    import { navigate } from "svelte-routing";

    let dropdownOpen = false;
    let debounceTimeout;
	let inputFocused = false;

    const hardcodedUnions = [
        { name: 'Brf Stockholm Esplanad', addresses: ['Settergatan 8, Norra Djurgårdsstaden, Stockholm'], coreId: '193' },
        { name: 'Brf Vasagatan', addresses: ['Kungsvägen 31, Säter'], coreId: '331' },
        { name: 'Brf Sjölejonet 1 & 2', addresses: ['Döbelnsgatan 53A, Vasastan, Stockholm'], coreId: '156' },
        { name: 'Brf Stockholm Manteln 6', addresses: ['Hägerstensvägen 207-209, Hägersten'], coreId: '191' },
        { name: 'Brf Rb Fajansen', addresses: ['Skaragatan 9, Lidköping'], coreId: '221' },
        { name: 'Brf Jansbo', addresses: ['Stenhålsgatan 10, Borlänge'], coreId: '322' },
        { name: 'Brf M 6', addresses: ['Heleneborgsgatan 9A, Södermalm, Stockholm'], coreId: '126' },
        { name: 'Brf Ljuset', addresses: ['Åsögatan 176, Södermalm , Stockholm'], coreId: '152' },
        { name: 'Brf Hsb Broparken', addresses: ['Labradorgatan 30, Norra Djurgårdsstaden, Stockholm'], coreId: '178' },
        { name: 'Brf Hsb Bäckahästen I Stockholm', addresses: ['Stagneliusvägen 30, Stockholm'], coreId: '189' }
    ];

	let wasLoading = get(brfListLoading);
	$: {
		if (wasLoading && !$brfListLoading) {
			if (inputFocused || $searchTerm) {
				dropdownOpen = true;
			}
		}
		wasLoading = $brfListLoading;
	}

    // Helper function to create URL-friendly slugs
    function slugify(text) {
        return text
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-')
            .replace(/-$/, '');
    }

    $: filteredItems = $objects
		? $objects
			.map(item => {
				return {
					...item,
					name: item.name
				};
			})
			.filter(item => {
				const searchTermLower = $searchTerm.toLowerCase();
				const nameMatch = item.name.toLowerCase().includes(searchTermLower);
				const addressMatch = item.addresses?.some(address =>
					address?.toLowerCase().includes(searchTermLower)
				) ?? false;
				const orgNumberMatch = item.org_number?.toLowerCase().includes(searchTermLower) ?? false;
				return nameMatch || addressMatch || orgNumberMatch;
			})
			.slice(0, 10)
		: []; // Return empty array if $objects is undefined

    function handleInput(e) {
		clearTimeout(debounceTimeout);
		debounceTimeout = setTimeout(() => {
			fetchBrfs($searchTerm);
		}, 300);
	}

    function selectItem(item) {

		redovisning.set(item.fileName);
		searchTerm.set(item.name);
		dropdownOpen = false;
        answer.set("");
        question.set("");
		
		// Create SEO-friendly URL with both ID and name
		const nameSlug = slugify(item.name);
		navigate(`/brf/${item.coreId}/${nameSlug}`);
		
		requestAnimationFrame(() => {
            setTimeout(() => {
                window.scrollTo(0, 0); // Changed to scroll to top instead of 500
            }, 100);
        });
	}

    function showAddress(item, searchTerm){
		if (!item || !Array.isArray(item.addresses)) {
			return null;
		}

		const searchValue = get(searchTerm)?.toLowerCase();
		if (!searchValue) {
			return item.addresses[0];
		}

		const normalizedSearchValue = searchValue.replace(/\s+/g, '').replace(/[^a-z0-9]/gi, ''); // Normalize the search term by removing spaces and special characters
		const matchingAddress = item.addresses.find(address => { // Find the first matching address
			const normalizedAddress = address?.toLowerCase()?.replace(/\s+/g, '')?.replace(/[^a-z0-9]/gi, ''); // Normalize the address for comparison
			return normalizedAddress?.includes(normalizedSearchValue) || null;
		});

		if (matchingAddress){
			return matchingAddress
		}
		return item?.addresses[0];
	}

	function highlightMatch(text, searchTerm) {
        const term = get(searchTerm).toLowerCase();
        if (!term) return text; // No search term, return full text

        const regex = new RegExp(`(${term})`, "gi"); // Case-insensitive match
        return text.replace(regex, '<span class="highlight">$1</span>'); // Wrap match in span
    }
</script>

<div class="dropdown" on:focusin={() => dropdownOpen = true} on:focusout={() => dropdownOpen = false}>
    <input
        type="text" 
        bind:value={$searchTerm}
        on:input={handleInput}
        on:focus={() => inputFocused = true}
        on:blur={() => inputFocused = false}
        class="dropdown-search-input"
        placeholder="Sök förening eller adress"
    />
    {#if $brfListLoading && dropdownOpen}
        <div class="dropdown-content">
            {#each hardcodedUnions as item}
                <div class="dropdown-item" on:mousedown={() => selectItem(item)}>
                    {@html highlightMatch(item.name, searchTerm)}
                    {#if showAddress(item, searchTerm)}
                    <br>
                    <div class="drop-down-adress">
                        {@html highlightMatch(showAddress(item, searchTerm), searchTerm)}
                    </div>
                    {/if}
                </div>
            {/each}
        </div>
    {:else if dropdownOpen && filteredItems.length > 0}
        <div class="dropdown-content">
            {#each filteredItems as item}
            <div class="dropdown-item" on:mousedown={() => selectItem(item)}>
                {@html highlightMatch(item.name, searchTerm)}
                {#if showAddress(item, searchTerm) || item.org_number}
                    <br>
                    <div class="drop-down-adress">
                        {#if showAddress(item, searchTerm)}
                            {@html highlightMatch(showAddress(item, searchTerm), searchTerm)}
                        {/if}
                        {#if item.org_number}
                            {#if showAddress(item, searchTerm)} · {/if}
                            {@html highlightMatch(item.org_number, searchTerm)}
                        {/if}
                    </div>
                {/if}
                </div>
            {/each}
        </div>
    {/if}
</div>